import {
    GrowthBook,
    BrowserCookieStickyBucketService
} from '@growthbook/growthbook';
import Cookies from 'js-cookie';
import TrackingEventsEnum from '@/enums/modules/TrackingEventsEnum';

export default {
    install(Vue) {
        const eventQueue = [];
        let flushInterval = null;
        let pollingStartTime = null;
        const MAX_POLLING_TIME = 60000;
        const MAX_QUEUE_SIZE = 200;
        const MAX_RETRY_ATTEMPTS = 3;

        const isAnalyticsReady = () => !!Vue.prototype.$analytics;

        const pushEvent = eventPayload => {
            if (eventQueue.length >= MAX_QUEUE_SIZE) {
                eventQueue.shift();
            }
            eventPayload._attempts = 0;
            eventQueue.push(eventPayload);
        };

        const flushEventQueue = () => {
            if (isAnalyticsReady() && eventQueue.length > 0) {
                let hasError = false;

                while (eventQueue.length > 0 && !hasError) {
                    const event = eventQueue.shift();

                    try {
                        Vue.prototype.$analytics.track(
                            TrackingEventsEnum.VIEWED_EXPERIMENT,
                            event
                        );
                    } catch (error) {
                        event._attempts = (event._attempts || 0) + 1;

                        if (event._attempts < MAX_RETRY_ATTEMPTS) {
                            eventQueue.unshift(event);
                            hasError = true;
                        } else if (import.meta.env.DEV) {
                            console.warn(
                                'Failed to track experiment after retries:',
                                error
                            );
                        }
                    }
                }

                if (!hasError && flushInterval) {
                    clearInterval(flushInterval);
                    flushInterval = null;
                    pollingStartTime = null;
                }
            } else if (
                pollingStartTime &&
                Date.now() - pollingStartTime > MAX_POLLING_TIME
            ) {
                if (flushInterval) {
                    clearInterval(flushInterval);
                    flushInterval = null;
                }
            }
        };

        // Initialize GrowthBook with user attributes
        const growthbook = new GrowthBook({
            clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY,
            apiHost: import.meta.env.VITE_GROWTHBOOK_API_HOST,
            enableDevMode: !import.meta.env.PROD,
            attributes: {
                email: null,
                venueId: null,
                venueSlug: null,
                accountId: null
            },
            stickyBucketService: new BrowserCookieStickyBucketService({
                jsCookie: Cookies
            }),
            trackingCallback: (experiment, result) => {
                const eventPayload = {
                    experimentKey: experiment.key,
                    variationId: result.variationId,
                    venueId: growthbook.getAttributes()?.venueId,
                    accountId: growthbook.getAttributes()?.accountId,
                    venueSlug: growthbook.getAttributes()?.venueSlug
                };

                if (isAnalyticsReady()) {
                    try {
                        Vue.prototype.$analytics.track(
                            TrackingEventsEnum.VIEWED_EXPERIMENT,
                            eventPayload
                        );
                    } catch (error) {
                        if (import.meta.env.DEV) {
                            console.warn('Failed to track experiment:', error);
                        }
                    }
                } else {
                    pushEvent(eventPayload);

                    if (!flushInterval) {
                        pollingStartTime = Date.now();
                        flushInterval = setInterval(flushEventQueue, 200);
                    }
                }
            }
        });

        growthbook.flushExperimentQueue = flushEventQueue;

        // Initialize Growthbook and load features
        growthbook
            .init({
                timeout: 10000,
                streaming: false
            })
            .catch(err => {
                console.error('Failed to load Growthbook features:', err);
            });

        // Add methods to update attributes
        growthbook.updateAttributes = attrs => {
            growthbook.setAttributes({
                ...growthbook.getAttributes(),
                ...attrs
            });
        };

        // Attach to Vue prototype
        Vue.prototype.$growthbook = growthbook;
    }
};
